import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import api, { isInternal } from "@/lib/api";
import { fmtDate } from "@/components/DateInput";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { DatabaseBackup, Download, Trash2, ShieldCheck, CloudUpload } from "lucide-react";

export default function ManageData() {
  const { user, selectedClientId, clients } = useAuth();
  const { t } = useI18n();
  const clientId = isInternal(user.role) ? selectedClientId : user.client_id;
  const client = clients.find((c) => c.id === clientId);
  const [backups, setBackups] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!clientId) return;
    const { data } = await api.get(`/backup/list?client_id=${clientId}`);
    setBackups(data);
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const backupNow = async () => {
    if (!clientId) return;
    setBusy(true);
    try {
      const res = await api.get(`/backup/export?client_id=${clientId}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `backup_${client?.code || "data"}.xlsx`; a.click();
      URL.revokeObjectURL(url);
      toast.success(t("common.saved")); load();
    } catch (e) { toast.error("Gagal membuat backup"); } finally { setBusy(false); }
  };
  const download = async (b) => {
    try {
      const res = await api.get(`/backup/${b.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = b.filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Gagal mengunduh backup"); }
  };
  const del = async (b) => {
    if (!window.confirm(`Hapus catatan backup ${b.filename}?`)) return;
    await api.delete(`/backup/${b.id}`); toast.success(t("common.saved")); load();
  };

  const fmtSize = (n) => (n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

  return (
    <div className="space-y-4" data-testid="managedata-page">
      <div className="page-sticky flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-head text-2xl font-bold tracking-tight">{t("md.title")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t("md.desc")} {client && `· ${client.name}`}</p>
        </div>
        <Button onClick={backupNow} disabled={busy || !clientId} data-testid="backup-now-btn">
          <DatabaseBackup className="h-4 w-4 mr-2" /> {busy ? "..." : t("md.backupNow")}
        </Button>
      </div>

      <Card className="p-4 flex items-start gap-3 bg-secondary/30">
        <ShieldCheck className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
        <p className="text-sm text-muted-foreground">{t("md.autoCloud")}</p>
      </Card>

      <Card className="p-4">
        <h2 className="font-head font-semibold mb-3 flex items-center gap-2"><CloudUpload className="h-4 w-4" /> {t("md.cloudBackups")}</h2>
        <div className="overflow-auto rounded-md border border-border">
          <Table>
            <TableHeader><TableRow className="bg-secondary/50">
              <TableHead>{t("common.date") || "Tanggal"}</TableHead><TableHead>File</TableHead>
              <TableHead className="text-right">{t("md.size")}</TableHead><TableHead>Cloud</TableHead>
              <TableHead className="text-right">{t("common.actions")}</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {backups.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-10 text-muted-foreground text-sm">{t("md.noBackups")}</TableCell></TableRow>}
              {backups.map((b) => (
                <TableRow key={b.id} data-testid={`backup-row-${b.id}`}>
                  <TableCell className="num text-xs">{fmtDate(b.created_at)} {(b.created_at || "").slice(11, 16)}</TableCell>
                  <TableCell className="text-xs">{b.filename}</TableCell>
                  <TableCell className="num text-right text-xs">{fmtSize(b.size)}</TableCell>
                  <TableCell className="text-xs">{b.cloud ? <span className="text-emerald-600">✓</span> : <span className="text-muted-foreground">—</span>}</TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button variant="ghost" size="icon" className="h-7 w-7" title={t("md.download")} onClick={() => download(b)} disabled={!b.cloud} data-testid={`backup-download-${b.id}`}><Download className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => del(b)} data-testid={`backup-del-${b.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
